import { useEffect, useState } from "react";
import { Button, Checkbox, Form, Input, message } from "antd";
import { FileDoneOutlined } from "@ant-design/icons";

import styles from "./FormTask.module.css";

import { Task } from "../../../../types/task";
import { getUserLogged } from "../../../../services/cognito";
import { useAuthContext, useMessageContext, useModalContext, useTaskFormContext } from "../../../../providers";

import { Maybe } from "../../../../types/common";
import { FormTask, UpdateTask } from "../../../../types/formTasks";
import { useForm } from "antd/es/form/Form";

import useMasterData from "../../../../hooks/useMasterData";
import { tasksFetcher } from "../../../../services/api";
import UploadSection from "../UploadSection";
import { CRITERIA_OPTIONS } from "../../../../utils/constants";

const TaskForm = ({
  taskToUpdate,
  addTask,
}: {
  taskToUpdate?: Maybe<Task>;
  addTask?: (task: Task) => void;
}) => {
  const [isSendingTask, setIsSendingTask] = useState(false);
  const [form] = useForm();
  
  const { logout } = useAuthContext()
  const { sendMessage } = useMessageContext();
  const { closeModal } = useModalContext();
  const { taskDocuments } = useTaskFormContext();
  const { getProduct } = useMasterData();

  useEffect(() => {
    if (taskToUpdate) {
      form.setFieldsValue({
        name: taskToUpdate.name,
        criteria: taskToUpdate.criteria,
      });
    }
  }, [taskToUpdate, form]);

  const onSubmit = async () => {
    try {
      await form.validateFields();
    } catch (error) {
      // Los campos del formulario tipo Input no son validos
      // isValidFormDocumentation();
      return;
    }

    // if (!isValidFormDocumentation()) return;

    const { getFieldValue } = form;

    taskDocuments.AR_FAR.FAR = '';
    taskDocuments.RPP_JT[0].RPP = taskDocuments.AR_FAR.AR;
    taskDocuments.AR_FAR.AR = '';
    taskDocuments.RPP_JT[0].JT = '';
    taskDocuments.RPP_JT[0].id = '';

    const userName = getUserLogged()?.getUsername();

    if (!userName) {
      sendMessage("La teva sessió ha expirat, has de tornar a iniciar sessió", "info", 2);
      return logout();
    }

    try {
      setIsSendingTask(true);

      if (taskToUpdate) {
        const updatePayload: UpdateTask = {
          id: taskToUpdate.id,
          name: getFieldValue("name"),
          criteria: getFieldValue("criteria"),
        };

        const maybeProduct = getProduct(getFieldValue("productName"));
        if (maybeProduct) {
          updatePayload.product = maybeProduct;
        }

        const newRpp = taskDocuments.RPP_JT?.[0]?.RPP;
        if (newRpp) {
          updatePayload.documents = taskDocuments;
          updatePayload.doc_name = newRpp;
        }

        await tasksFetcher.reuploadTask(updatePayload);
        message.success(`La tasca "${updatePayload.name}" s'ha recarregat correctament. El processament està en curs.`);
        closeModal();
      } else {
        // Creación de nueva tarea
        const task: FormTask = {
          name: getFieldValue("name"),
          doc_name: taskDocuments.RPP_JT[0].RPP ?? 'doc.pdf',
          product: getProduct(getFieldValue("productName")),
          documents: taskDocuments,
          criteria: getFieldValue("criteria"),
        };
        const taskCreated = await tasksFetcher.createTask(task);
        addTask && addTask(taskCreated);
        message.success(`La tasca "${task.name}" s'ha creat correctament. El processament està en curs.`);
        closeModal();
      }
    } catch (error) {
      console.error(error);
      message.error(`Error al ${taskToUpdate ? 'recarregar' : 'crear'} la petició d'anonimització. Si us plau, torna a intentar-ho més tard`);
    } finally {
      setIsSendingTask(false);
    }
  };

  return (
    <>
      {isSendingTask ?
      <div className={styles.loadingContainer}>
        <FileDoneOutlined className={styles.fileIcon}/>
        <strong>Estem carregant el teu document.</strong>
        <span>Si us plau espera uns instants</span>
      </div>
      :
      <Form form={form} layout="vertical">
        <h4 className={`head4 ${styles.modalTitle}`}>{taskToUpdate ? 'Recarregar tasca d\'anonimització' : 'Nova tasca d\'anonimització'}</h4>
        <Form.Item
          label={<strong>Nom de la tasca</strong>}
          name="name"
          rules={[
            { required: true, message: "Aquest camp és obligatori, si us plau Completar" },
            { 
              pattern: /^[^/]*$/, 
              message: "El nom de la tasca no pot contenir el caràcter /" 
            }
          ]}
        >
          <Input placeholder="Introduir Nom de la tasca per a identificar la tasca" />
        </Form.Item>
        <Form.Item
          label={<strong>Criteris d'anonimització</strong>}
          name="criteria"
          rules={[{ required: true, message: "Aquest camp és obligatori, si us plau Completar" }]}
        >
          <Checkbox.Group>
            <div className={styles.checkboxeGroup}>
              {CRITERIA_OPTIONS.map((option) => (
                <Checkbox key={option.value} value={option.value} className={styles.checkbox}>
                  {option.label}
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
        </Form.Item>
        <UploadSection
          required={!taskToUpdate}
          initialFileName={taskToUpdate?.doc_name}
        />
        <Button loading={isSendingTask} onClick={onSubmit} type="primary" block>{taskToUpdate ? 'Recarregar' : 'Crear'}</Button>
      </Form>}
    </>
  );
};
export default TaskForm;
